import { motion } from 'framer-motion';
import { Target, Eye, Heart } from 'lucide-react';

const aboutItems = [
  {
    icon: Target,
    title: 'Our Mission',
    description:
      'To deliver innovative technology solutions that help businesses thrive in the digital era through excellence, integrity, and customer-focused cafeteria automation.',
    color: 'bg-[#FFC000]',
    span: 'col-span-1 md:col-span-2',
    textcolor: 'text-[#0066CC]',
  },
  {
    icon: Eye,
    title: 'Our Vision',
    description:
      'To be a globally recognized leader in digital cafeteria solutions, empowering organizations to achieve sustainable growth through seamless dining experiences.',
    color: 'bg-[#F5A623]',
    span: 'col-span-1 md:col-span-2',
    textcolor: 'text-[#FFC000]',
  },
  {
    icon: Heart,
    title: 'Our Values',
    description:
      'Integrity, Innovation, Excellence, Customer Focus, and Sustainable Growth are the core values that guide our every action in transforming corporate dining.',
    color: 'bg-[#0066CC]',
    span: 'col-span-1 md:col-span-4',
    textcolor: 'text-[#F5A623]',
  },
];

const About = () => {
  return (
    <section
      id="about"
      className="section-padding bg-white"
      data-testid="about-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="section-header"
        >
          <span className="text-[#0066CC] text-sm font-semibold uppercase tracking-wider mb-4 block">
            About Us
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Transforming Cafeteria Dining Experience
          </h2>
          <div className="accent-bar mx-auto mb-6" />
          <p className="text-base md:text-lg text-gray-600 leading-relaxed">
            MyCafé Connect is a comprehensive cafeteria digitization platform by
            TISTL, offering online food ordering, digital payments, billing,
            analytics, and reporting for fast, secure, cashless dining.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {aboutItems.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`${item.span} bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100 card-hover group`}
              data-testid={`about-card-${item.title.toLowerCase().replace(' ', '-')}`}
            >
              <div
                className={`w-14 h-14 ${item.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
              >
                <item.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className={`text-xl md:text-2xl ${item.textcolor} font-bold mb-4`}>
                {item.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Team Image Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://s3-cc-prod.s3.ap-south-1.amazonaws.com/tistl/Innovative+Software+Solutions.png"
                alt="Team collaboration"
                className="w-full h-[300px] md:h-[400px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#F5A623]/20 rounded-2xl -z-10" />
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-[#0066CC]/10 rounded-2xl -z-10" />
          </div>

          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              A Technology-Driven Partner for Your Cafeteria Needs
            </h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              TISTL is committed to delivering innovative software solutions and
              IT services that drive business growth. Our MyCafé Connect
              platform is designed to streamline cafeteria operations, enhance
              employee satisfaction, and provide actionable insights through
              advanced analytics.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We partner with organizations of all sizes—from startups to
              enterprise campuses—to create modern, tech-enabled dining
              experiences that are smarter, faster, and built for the future of
              work.
            </p>

            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="text-3xl font-bold text-[#0066CC]">20+</div>
                <div className="text-sm text-gray-600 mt-1">Years of Industry Experience</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="text-3xl font-bold text-[#F5A623]">20+</div>
                <div className="text-sm text-gray-600 mt-1">Happy Clients</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
